import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export type CongressExportFieldKey =
  | 'created_at'
  | 'first_name'
  | 'last_name'
  | 'full_name'
  | 'institution'
  | 'profession'
  | 'license_type'
  | 'license_number'
  | 'email'
  | 'phone'
  | 'payment_type'
  | 'sponsor_name'
  | 'needs_accommodation'
  | 'accommodation_dates'
  | 'room_type'
  | 'roommate_name'
  | 'needs_transport';

export interface CongressExportField {
  key: CongressExportFieldKey;
  label: string;
}

export const CONGRESS_EXPORT_FIELDS: CongressExportField[] = [
  { key: 'created_at', label: 'Datum prijave' },
  { key: 'first_name', label: 'Ime' },
  { key: 'last_name', label: 'Prezime' },
  { key: 'full_name', label: 'Ime i prezime' },
  { key: 'institution', label: 'Institucija' },
  { key: 'profession', label: 'Profesija' },
  { key: 'license_type', label: 'Vrsta licence' },
  { key: 'license_number', label: 'Broj licence' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Telefon' },
  { key: 'payment_type', label: 'Način plaćanja' },
  { key: 'sponsor_name', label: 'Sponzor' },
  { key: 'needs_accommodation', label: 'Smještaj' },
  { key: 'accommodation_dates', label: 'Datumi smještaja' },
  { key: 'room_type', label: 'Vrsta sobe' },
  { key: 'roommate_name', label: 'Osoba u sobi' },
  { key: 'needs_transport', label: 'Prijevoz' }
];

export const DEFAULT_CONGRESS_EXPORT_FIELDS: CongressExportFieldKey[] = [
  'created_at',
  'first_name',
  'last_name',
  'institution',
  'profession',
  'email',
  'phone',
  'payment_type',
  'needs_accommodation',
  'accommodation_dates',
  'room_type',
  'needs_transport'
];

export interface CongressExportRow {
  created_at?: string;
  first_name?: string;
  last_name?: string;
  institution?: string;
  profession?: string;
  license_type?: string;
  license_number?: string;
  email?: string;
  phone?: string;
  payment_type?: string;
  sponsor_name?: string;
  needs_accommodation?: boolean;
  accommodation_dates?: string[] | null;
  room_type?: string | null;
  roommate_name?: string | null;
  needs_transport?: boolean;
}

const professionLabels: Record<string, string> = {
  nurse: 'Medicinska sestra/tehničar',
  doctor: 'Liječnik',
  pharmacy: 'Farmacija',
  other: 'Ostalo'
};

const paymentLabels: Record<string, string> = {
  personal: 'Osobno',
  institution: 'Zdravstvena ustanova',
  sponsor: 'Sponzor'
};

const roomTypeLabels: Record<string, string> = {
  single: 'Jednokrevetna',
  double: 'Dvokrevetna'
};

function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('hr-HR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function yesNo(value?: boolean) {
  if (value === true) return 'Da';
  if (value === false) return 'Ne';
  return '';
}

export function getCongressFieldValue(
  row: CongressExportRow,
  key: CongressExportFieldKey
): string {
  switch (key) {
    case 'created_at':
      return formatDate(row.created_at);
    case 'first_name':
      return row.first_name || '';
    case 'last_name':
      return row.last_name || '';
    case 'full_name':
      return [row.first_name, row.last_name].filter(Boolean).join(' ');
    case 'institution':
      return row.institution || '';
    case 'profession':
      return (
        (row.profession && professionLabels[row.profession]) ||
        row.profession ||
        ''
      );
    case 'license_type':
      if (row.profession === 'nurse') return 'HKMS';
      if (row.profession === 'doctor') return 'HLK';
      return row.license_type || '';
    case 'license_number':
      return row.license_number || '';
    case 'email':
      return row.email || '';
    case 'phone':
      return row.phone || '';
    case 'payment_type':
      return (
        (row.payment_type && paymentLabels[row.payment_type]) ||
        row.payment_type ||
        ''
      );
    case 'sponsor_name':
      return row.sponsor_name || '';
    case 'needs_accommodation':
      return yesNo(row.needs_accommodation);
    case 'accommodation_dates':
      return Array.isArray(row.accommodation_dates)
        ? row.accommodation_dates.join(', ')
        : '';
    case 'room_type':
      return (
        (row.room_type && roomTypeLabels[row.room_type]) ||
        row.room_type ||
        ''
      );
    case 'roommate_name':
      return row.roommate_name || '';
    case 'needs_transport':
      return yesNo(row.needs_transport);
    default:
      return '';
  }
}

function buildRows(
  data: CongressExportRow[],
  selectedFields: CongressExportFieldKey[]
) {
  const fields = CONGRESS_EXPORT_FIELDS.filter((f) =>
    selectedFields.includes(f.key)
  );
  const headers = fields.map((f) => f.label);
  const rows = data.map((row) =>
    fields.map((field) => getCongressFieldValue(row, field.key))
  );
  return { fields, headers, rows };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportCongressToExcel(
  data: CongressExportRow[],
  selectedFields: CongressExportFieldKey[],
  filename = 'prijave-kongres.xlsx'
) {
  const { headers, rows } = buildRows(data, selectedFields);
  const sheetData = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  const colWidths = headers.map((header, colIndex) => {
    const maxLen = Math.max(
      header.length,
      ...rows.map((row) => String(row[colIndex] || '').length)
    );
    return { wch: Math.min(Math.max(maxLen + 2, 12), 40) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Prijave');
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });
  downloadBlob(
    new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    filename
  );
}

export function exportCongressToPdf(
  data: CongressExportRow[],
  selectedFields: CongressExportFieldKey[],
  filename = 'prijave-kongres.pdf'
) {
  const { headers, rows } = buildRows(data, selectedFields);
  const doc = new jsPDF({
    orientation: selectedFields.length > 6 ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  doc.setFontSize(14);
  doc.text('Prijave za Kongres HDOMST 2026', 14, 15);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(
    `Izvezeno: ${new Date().toLocaleString('hr-HR')} · Ukupno: ${data.length}`,
    14,
    21
  );
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 26,
    head: [headers],
    body: rows,
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    },
    margin: { left: 10, right: 10 }
  });

  doc.save(filename);
}
